import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { catchError, Observable, of, tap } from 'rxjs';
import { isAdmin } from 'src/app/core/misc/isAdmin';
import { LundifySubscriptionsService } from 'src/app/core/services/lundify-subscriptions.service';
import { AuthSelectors } from 'src/app/core/state/auth/auth.selectors';
import { FirmSelectors } from 'src/app/core/state/firm/firm.selectors';
import { AppModuleType } from 'src/app/core/state/types/collaboration-package.types';
import { LundifySubscriptionResponse } from 'src/app/core/state/types/lundify-subscription-response.types';

@Injectable()
export class LundifySubscriptionFacade {
  private store: Store = inject(Store);
  private lundifySubscriptionsService: LundifySubscriptionsService = inject(LundifySubscriptionsService);
  private firmCustomerNumber: Signal<number> = this.store.selectSignal(FirmSelectors.selectFirmCustomerNbr);
  private internalSubscriptionData: WritableSignal<LundifySubscriptionResponse | null> = signal(null);
  private loading = signal(true);
  private error: WritableSignal<'none' | 'general' | 'markedForDeletion'> = signal('none');

  private authUser = toSignal(this.store.select(AuthSelectors.selectAuthUser));
  private isAdminUser = computed(() => isAdmin(this.authUser()));

  get subscriptionData(): Signal<LundifySubscriptionResponse | null> {
    return this.internalSubscriptionData;
  }

  isLoading = computed(() => this.loading());
  hasError = computed(() => this.error() !== 'none');
  errorType = computed(() => this.error());

  isClientPaying = computed(() => this.internalSubscriptionData()?.isClientPaying ?? false);
  subscriptionPayingCustomerNbr = computed(() => this.internalSubscriptionData()?.payingCustomerNumber ?? null);
  subscriptionFirmCustomerNbr = computed(() => this.internalSubscriptionData()?.firmCustomerNumber ?? null);
  expenseInformation = computed(() => this.internalSubscriptionData()?.subscriptionExpenseSnapshot || null);
  publicKey = computed(() => this.internalSubscriptionData()?.publicKey || null);
  isArchived = computed(() => this.internalSubscriptionData()?.isArchived || false);
  isFirmSubscriptionType = computed(() => [4, 6].includes(this.internalSubscriptionData()?.subscriptionTypeId ?? 0));
  hasSubscriptionTypeId = computed(() => this.internalSubscriptionData()?.subscriptionTypeId !== null);

  hasPermissionToEdit = (responsibleUserId: number) =>
    computed(() => this.isAdminUser() === true || this.authUser().id === responsibleUserId);

  hasSubscriptionData = computed(() => !!this.internalSubscriptionData());

  payingPart = computed(() => {
    const isClientPaying = this.internalSubscriptionData()?.isClientPaying;
    // eslint-disable-next-line no-undefined
    if (isClientPaying === undefined) {
      return null;
    }
    return isClientPaying ? 'client' : 'firm';
  });

  isCollaboratorFirm = computed(() => {
    const subscriptionFirmCustomerNbr = this.subscriptionFirmCustomerNbr();
    const firmCustomerNbr = this.firmCustomerNumber();
    if (subscriptionFirmCustomerNbr === null || firmCustomerNbr === null) {
      return null;
    }
    return +subscriptionFirmCustomerNbr !== +firmCustomerNbr;
  });

  isAnnualBundle = computed(() => {
    const bundle = this.getBundleModule();
    return bundle?.blaProductId.toLowerCase() === 'appannual';
  });

  isMiniOrAnnualBundle = computed(() => {
    const bundle = this.getBundleModule();
    if (!bundle) {
      return false;
    }
    const isMini = bundle.blaProductId.toLowerCase() === 'appmini';
    const isAnnual = bundle.blaProductId.toLowerCase() === 'appannual';
    return isMini || isAnnual;
  });

  bundleName = computed(() => {
    const bundle = this.getBundleModule();
    return bundle?.displayName || '--';
  });

  bundleId = computed(() => {
    const bundle = this.getBundleModule();
    return bundle?.blaProductId || null;
  });

  countryCode = computed(() => {
    return this.internalSubscriptionData()?.countryCode || null;
  });

  hasEmailPortalIntegration = computed(() => {
    const expenseSnapshot = this.internalSubscriptionData()?.subscriptionExpenseSnapshot;
    if (!expenseSnapshot) {
      return false;
    }
    return (expenseSnapshot.integrationExpenseSnapshot ?? []).some(
      (snapshot) => snapshot.blaProductId.toLowerCase() === 'blaep',
    );
  });

  loadSubscriptionInformation(clientId: number | string): Observable<LundifySubscriptionResponse> {
    this.loading.set(true);
    this.error.set('none');

    return this.lundifySubscriptionsService.loadSubscriptionInformation(clientId).pipe(
      tap((data) => {
        this.internalSubscriptionData.set(data);
        this.loading.set(false);
      }),
      catchError((error: unknown) => {
        this.internalSubscriptionData.set(null);
        this.error.set('general');

        if (error && typeof error === 'object' && 'error' in error) {
          const err = (error as { error: { message: string } }).error;
          if (err.message === 'DATABASE_MARKED_FOR_DELETION') {
            this.error.set('markedForDeletion');
          }
        }
        return of(null as unknown as LundifySubscriptionResponse);
      }),
    );
  }

  updatePayingPart(clientId: number | string, body: { reference: string; email: string; phoneNumber: string } | null) {
    return this.lundifySubscriptionsService
      .updatePayingPart(clientId, body)
      .pipe(tap((data) => this.internalSubscriptionData.update((state) => ({ ...state, ...data }))));
  }

  resetSubscriptionData(): void {
    this.internalSubscriptionData.set(null);
  }

  private getBundleModule() {
    const modules = this.internalSubscriptionData()?.modules || [];
    return modules.find((module) => module.type === AppModuleType.BUNDLE) || null;
  }
}
